import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../auth/AuthContext";
import { toast } from "sonner";
import { User, Phone, Bell } from "lucide-react";
import ProfileLayout from "../components/ProfileLayout";

export default function ProfileSettings() {
  const { user, checkAuth } = useAuth();
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [dob, setDob] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [country, setCountry] = useState("CA");
  const [smsStatus, setSmsStatus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user) return;
    setDisplayName(user.display_name || "");
    setPhone(user.phone || "");
    setDob(user.date_of_birth || "");
    setSmsOptIn(!!user.sms_opt_in);
    setCountry((user.country || "CA").toUpperCase());
  }, [user]);

  useEffect(() => {
    axios.get(`${API}/auth/sms/status`).then((r) => setSmsStatus(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    if (smsOptIn && !phone.trim()) {
      toast.error("Add a mobile number to enable SMS alerts");
      return;
    }
    setSaving(true);
    try {
      const dobLocked = user?.kyc_status === "approved";
      await axios.patch(
        `${API}/auth/profile`,
        {
          display_name: displayName.trim() || null,
          phone: phone.trim() || null,
          // Omit DoB when locked so saving other fields doesn't trip the KYC lock (tracker #21).
          ...(dobLocked ? {} : { date_of_birth: dob.trim() || null }),
          sms_opt_in: smsOptIn,
          country: country.trim().toUpperCase() || "CA",
        },
        { withCredentials: true },
      );
      await checkAuth?.();
      toast.success(smsOptIn ? "Profile saved — SMS alerts enabled" : "Profile updated");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "invalid_phone") {
        toast.error("Use a valid mobile number (e.g. +1 555 123 4567)");
      } else if (detail === "phone_required_for_sms") {
        toast.error("Add a mobile number before enabling SMS alerts");
      } else {
        toast.error(detail || "Could not save profile");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <ProfileLayout title="Profile settings" eyebrow="Account">
      <p className="text-[var(--zc-text-2)] text-sm -mt-4 mb-6" data-testid="profile-settings-page">
        Legal name from sign-in: <b className="text-[var(--zc-text)]">{user?.name}</b>. Display name is how hosts see you on trips.
      </p>

      <div className="zc-card p-6 space-y-5">
        <label className="block" htmlFor="profile-display-name">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--zc-text-3)] mb-2">
            <User size={12} /> Display name
          </div>
          <input
            id="profile-display-name"
            className="zc-input h-11 px-3 w-full text-sm"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeholder="How hosts see you"
            data-testid="profile-display-name"
          />
        </label>

        <label className="block" htmlFor="profile-phone">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-[var(--zc-text-3)] mb-2">
            <Phone size={12} /> Mobile number
          </div>
          <input
            id="profile-phone"
            type="tel"
            className="zc-input h-11 px-3 w-full text-sm"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            autoComplete="tel"
            data-testid="profile-phone"
          />
          <p className="zc-hint mt-1.5">
            Include your country code, e.g. +1 for Canada/US, +44 for UK.
            {smsStatus?.configured && (
              <span className="text-[var(--zc-success)]"> · Twilio SMS active</span>
            )}
          </p>
        </label>

        <label className="block" htmlFor="profile-dob">
          <div className="text-xs font-semibold uppercase tracking-widest text-[var(--zc-text-3)] mb-2">
            Date of birth
          </div>
          <input
            id="profile-dob"
            type="date"
            className="zc-input h-11 px-3 w-full text-sm disabled:opacity-60 disabled:cursor-not-allowed"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            disabled={user?.kyc_status === "approved"}
            data-testid="profile-dob"
          />
          <p className="zc-hint mt-1.5">
            {user?.kyc_status === "approved"
              ? "From your verified driver's licence — contact support to update."
              : "Required to book — you must be at least 24. Usually filled automatically from your driver's licence verification."}
          </p>
        </label>

        <label className="block" htmlFor="profile-country">
          <div className="text-xs font-semibold uppercase tracking-widest text-[var(--zc-text-3)] mb-2">
            Country of residence
          </div>
          <select
            id="profile-country"
            className="zc-input h-11 px-3 w-full text-sm"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="profile-country"
          >
            <option value="CA">Canada</option>
            <option value="US">United States</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
            <option value="IN">India</option>
            <option value="MX">Mexico</option>
            <option value="PH">Philippines</option>
          </select>
          <p className="zc-hint mt-1.5">
            A valid driver&apos;s licence is required for identity verification in all countries.
          </p>
        </label>

        <label htmlFor="profile-sms-opt-in" className={`flex items-start gap-3 ${phone.trim() ? "cursor-pointer" : "opacity-60"}`}>
          <input
            id="profile-sms-opt-in"
            type="checkbox"
            checked={smsOptIn}
            disabled={!phone.trim()}
            onChange={(e) => setSmsOptIn(e.target.checked)}
            className="mt-1"
            data-testid="profile-sms-opt-in"
          />
          <div>
            <div className="flex items-center gap-2 text-sm font-semibold">
              <Bell size={14} className="text-[var(--zc-accent)]" />
              SMS trip alerts
            </div>
            <p className="zc-hint mt-1">
              {smsStatus?.mock_mode
                ? "Twilio not configured on the server — set credentials in backend/.env and restart the API."
                : smsStatus?.test_mode
                  ? "Twilio test mode: API calls work but texts are not delivered to real phones. Use live credentials for production."
                  : "Get booking confirmations and trip updates by text, in addition to email."}
            </p>
            {!phone.trim() && (
              <p className="text-caption text-[var(--zc-warn,#f59e0b)] mt-1">Enter your mobile number above first.</p>
            )}
          </div>
        </label>

        <button
          onClick={save}
          disabled={saving}
          className="w-full h-11 zc-btn-primary text-sm disabled:opacity-60"
          data-testid="profile-save"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </ProfileLayout>
  );
}
